/**
 * NotificationsController — /api/v1/notifications
 * Mirrors: src/Aidepo.Api/Controllers/NotificationsController.cs
 *
 * Phase 3.10 — backend landed. Notifications are generated by a background
 * NotificationGenerator; PATCH records a user action (done / snooze / dismiss)
 * and, for replacement/safety kinds, advances the underlying ScheduleRule.
 */
import { apiGet, apiPatch } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';

/** Discriminator of a notification — drives the visual tone. */
export type NotificationKind = 'replacement' | 'safety' | string;

/** Action recorded against a notification. */
export type NotificationAction = 'done' | 'snooze' | 'dismiss';

/** One row of GET /api/v1/notifications. */
export interface NotificationRow {
  readonly id: Uuid;
  readonly kind: NotificationKind;
  readonly equipmentId: Uuid | null;
  readonly itemId: Uuid | null;
  readonly scheduledFor: IsoDateTime;
  readonly readAt: IsoDateTime | null;
  readonly actionTaken: string | null;
  readonly createdAt: IsoDateTime;
  /** Имя оборудования, к которому относится уведомление (aidepo-7). */
  readonly equipmentName?: string | null;
  /** Название правила регламента ТО (aidepo-7). */
  readonly ruleTitle?: string | null;
}

export interface ListNotificationsResponse {
  readonly notifications: readonly NotificationRow[];
}

export interface PatchNotificationResponse {
  readonly id: Uuid;
  readonly action: NotificationAction;
  readonly status: string;
}

/** GET /api/v1/notifications — list; unreadOnly filters to unread. */
export function listNotifications(unreadOnly = false): Promise<ListNotificationsResponse> {
  const query = unreadOnly ? '?unreadOnly=true' : '';
  return apiGet<ListNotificationsResponse>(`/api/v1/notifications${query}`);
}

/** PATCH /api/v1/notifications/{id} — record an action (done | snooze | dismiss). */
export function patchNotification(
  id: Uuid,
  action: NotificationAction,
): Promise<PatchNotificationResponse> {
  return apiPatch<PatchNotificationResponse, { action: NotificationAction }>(
    `/api/v1/notifications/${id}`,
    { action },
  );
}
